import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  InputAdornment,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import { catalogErrorText } from './bookCatalogErrors';

const AUTHOR_API = `${config.API_BASE_URL}/api/v1/content/admin/authors`;

const defaultForm = {
  name: '',
  bio: '',
  photoUrl: '',
  website: '',
  isActive: true,
};

// For the loading functions the mount effect calls: the hook's `t` is for rendering.
const tAuthors = (key, options) => i18n.t(key, { ns: 'authors', ...options });

const AuthorManagementPage = () => {
  const { t } = useTranslation('authors');
  const { getAccessToken } = useAuth();

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tAuthors('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Books-by-author drill-down
  // Upload an author photo to S3 (same endpoint books, events and content use) and keep the
  // returned URL as photoUrl. The text field stays editable so an existing URL can still be pasted.
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked again after a failure
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error(t('errors.authRequired'));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'author-photos');
      const res = await fetch(`${config.API_BASE_URL}/em/v1/admin/images/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }, // no Content-Type: the browser sets the boundary
        body: formData,
      });
      if (!res.ok) throw new Error(t('errors.uploadFailed'));
      const json = await res.json();
      const imageUrl = json?.data?.imageUrl;
      if (!imageUrl) throw new Error(t('errors.noImageUrl'));
      setForm((prev) => ({ ...prev, photoUrl: imageUrl }));
      toast.success(t('toast.photoUploaded'));
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.uploadPhotoFallback')));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const [booksDialogOpen, setBooksDialogOpen] = useState(false);
  const [authorBooks, setAuthorBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksAuthorName, setBooksAuthorName] = useState('');

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    setLoading(true);
    try {
      const res = await fetch(AUTHOR_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error(tAuthors('errors.fetchAuthors'));
      const data = await res.json();
      setAuthors(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(catalogErrorText(err, tAuthors('errors.fetchAuthors')));
    } finally {
      setLoading(false);
    }
  };

  const fetchBooksByAuthor = async (author) => {
    setBooksLoading(true);
    setBooksAuthorName(author.name);
    setBooksDialogOpen(true);
    try {
      const res = await fetch(`${AUTHOR_API}/${author.id}/books`, { headers: await authHeaders() });
      if (!res.ok) throw new Error(t('errors.fetchBooks'));
      const data = await res.json();
      setAuthorBooks(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.fetchBooksFallback')));
      setAuthorBooks([]);
    } finally {
      setBooksLoading(false);
    }
  };

  const openCreate = () => {
    setIsEdit(false);
    setSelectedAuthor(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = (author) => {
    setIsEdit(true);
    setSelectedAuthor(author);
    setForm({
      name: author.name || '',
      bio: author.bio || '',
      photoUrl: author.photoUrl || '',
      website: author.website || '',
      isActive: author.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const url = isEdit ? `${AUTHOR_API}/${selectedAuthor.id}` : AUTHOR_API;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: await authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          translateServerMessage(err.message, t(isEdit ? 'errors.updateFailed' : 'errors.createFailed'))
        );
      }
      toast.success(t(isEdit ? 'toast.updated' : 'toast.created'));
      setDialogOpen(false);
      fetchAuthors();
    } catch (err) {
      toast.error(catalogErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (author) => {
    if (!window.confirm(t('confirmDelete', { name: author.name }))) return;
    try {
      const res = await fetch(`${AUTHOR_API}/${author.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.deleted'));
      fetchAuthors();
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.deleteFallback')));
    }
  };

  const filteredAuthors = authors.filter((a) =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('header.title')}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('header.subtitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('header.refresh')}>
            <IconButton onClick={fetchAuthors} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('header.add')}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('search.placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.photo')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.bio')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.website')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.books')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAuthors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box py={4}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">{t('table.empty')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAuthors.map((author) => (
                  <TableRow
                    key={author.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => fetchBooksByAuthor(author)}
                  >
                    <TableCell>
                      <Avatar
                        src={author.photoUrl || undefined}
                        alt={author.name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {author.name?.[0]?.toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>{author.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {author.bio || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {author.website ? (
                        <Typography
                          variant="body2"
                          component="a"
                          href={author.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {author.website}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={author.isActive ? t('status.active') : t('status.inactive')}
                        color={author.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={author.bookCount ?? 0} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={t('table.edit')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(author)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('table.delete')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(author)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? t('dialog.editTitle') : t('dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2.5}>
            <TextField
              fullWidth
              label={t('dialog.name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth
              label={t('dialog.bio')}
              multiline
              rows={4}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label={t('dialog.photoUrl')}
                placeholder={t('dialog.photoPlaceholder')}
                value={form.photoUrl}
                onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>
                  ),
                }}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploadingPhoto}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 56, flexShrink: 0 }}
              >
                {uploadingPhoto ? t('dialog.uploading') : t('dialog.upload')}
                <input hidden type="file" accept="image/*" onChange={handlePhotoUpload} />
              </Button>
            </Box>
            {form.photoUrl && (
              <Box>
                <Avatar src={form.photoUrl} alt={t('dialog.photoPreview')} sx={{ width: 64, height: 64 }} />
              </Box>
            )}
            <TextField
              fullWidth
              label={t('dialog.website')}
              placeholder={t('dialog.websitePlaceholder')}
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><LinkIcon color="action" /></InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  color="success"
                />
              }
              label={form.isActive ? t('status.active') : t('status.inactive')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('dialog.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('dialog.saving') : isEdit ? t('dialog.update') : t('dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Books by Author Dialog */}
      <Dialog open={booksDialogOpen} onClose={() => setBooksDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('booksDialog.title', { name: booksAuthorName })}
          <IconButton size="small" onClick={() => setBooksDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {booksLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : authorBooks.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              {t('booksDialog.empty')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('booksDialog.bookTitle')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('booksDialog.isbn')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('booksDialog.price')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('booksDialog.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {authorBooks.map((book) => (
                  <TableRow key={book.id}>
                    <TableCell>{book.title}</TableCell>
                    <TableCell>{book.isbn || '—'}</TableCell>
                    <TableCell>${Number(book.price || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={book.isActive ? t('status.active') : t('status.inactive')}
                        color={book.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuthorManagementPage;
